import { useMemo } from 'react';
import { TableV2Column } from 'components/Table/TableV2';
import { TemplateItem } from 'modules/analyse-templates/types/TemplateItem';
import { Link, Stack } from '@mui/material';
import { formatDateFromNow } from 'utils/datetime';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import React from 'react';
import { TemplateSortField } from 'modules/analyse-templates/constants/sortConstants';
import { useIsAdmin } from 'hooks/useIsAdmin';
import Chip from '@mui/material/Chip';

export const useAnalysisTemplatesColumn = ({
  handleEditTemplate,
  handleDeleteTemplate,
  handleSetAsDefault,
  defaultAutomationTaskTemplateId
}: {
  handleEditTemplate: (templateId: string) => void;
  handleDeleteTemplate: (templateId: string) => void;
  handleSetAsDefault: (templateId: string) => void;
  defaultAutomationTaskTemplateId?: string | null;
}) => {
  const { isAdmin } = useIsAdmin();

  const columns = useMemo<TableV2Column<TemplateItem>[]>(() => {
    return [
      {
        id: TemplateSortField.TITLE,
        name: 'Template name',
        cell: (row) => (
          <Stack direction="row" gap={1}>
            {row.id === defaultAutomationTaskTemplateId && (
              <Chip label="Default" variant="inverse" color="primary" />
            )}
            <Link onClick={() => handleEditTemplate(row.id)} component="button">
              {row.title}
            </Link>
          </Stack>
        ),
        sortable: true,
        sortField: TemplateSortField.TITLE,
        sortFunction: (a, b) => a.title.localeCompare(b.title),
        width: '50%',
        grow: 2
      },
      {
        id: TemplateSortField.UPDATED_AT,
        name: 'Last updated',
        cell: (row) => formatDateFromNow(row.updatedAt),
        sortable: true,
        sortField: TemplateSortField.UPDATED_AT,
        sortFunction: (a, b) => {
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        },
        width: '20%'
      },
      {
        id: TemplateSortField.CREATED_BY,
        name: 'Created by',
        cell: (row) => row.user?.name,
        sortable: true,
        sortField: TemplateSortField.CREATED_BY,
        sortFunction: (a, b) => {
          const nameA = a.user?.name || '';
          const nameB = b.user?.name || '';
          return nameA.localeCompare(nameB);
        },
        width: '20%'
      },
      {
        id: 'actions',
        isAction: true,
        width: '10%',
        cell: (row) => {
          const options: ActionOption[] = [
            {
              label: 'Edit template',
              onClick: () => handleEditTemplate(row.id)
            }
          ];

          if (isAdmin && row.id !== defaultAutomationTaskTemplateId) {
            options.push({
              label: 'Set as default',
              onClick: () => handleSetAsDefault(row.id)
            });
          }
          if (isAdmin) {
            options.push({
              label: 'Delete',
              onClick: () => handleDeleteTemplate(row.id),
              itemColor: 'error.main'
            });
          } else if (row.id !== defaultAutomationTaskTemplateId) {
            options.push({
              label: 'Delete',
              onClick: () => handleDeleteTemplate(row.id),
              itemColor: 'error.main'
            });
          }

          return (
            <ActionPopover
              id={row.id}
              options={options}
              iconSx={{ color: 'primary.main' }}
            />
          );
        }
      }
    ];
  }, [
    handleEditTemplate,
    handleDeleteTemplate,
    handleSetAsDefault,
    defaultAutomationTaskTemplateId
  ]);

  return columns;
};
